//src/components/admin/UserManagement.tsx
import React, { useState, useEffect } from 'react';
import { 
  Users, Search, Mail, Shield, Plus, RefreshCw, 
  Database, UserCheck, CreditCard, Eye, Lock
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface User {
  id: string;
  email: string;
  role: string;
  company_name: string;
  membership_tier: string;
  subscription_status: string;
  created_at: string;
  total_count?: number;
}

export function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);

      // Try RPC function first (should work after fixes)
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_list_users', {
        p_search: searchTerm || null,
        p_role: null,
        p_limit: 100,
        p_offset: 0
      });

      if (!rpcError && rpcData) {
        setUsers(rpcData);
        console.log('✅ RPC function working! Loaded users:', rpcData.length);
        return;
      }

      // Fallback to direct table query
      console.warn('⚠️ RPC failed, using direct query:', rpcError?.message);
      
      const { data: directData, error: directError } = await supabase
        .from('profiles')
        .select(`
          id,
          email,
          role,
          company_name,
          membership_tier,
          subscription_status,
          created_at
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (directError) throw directError;
      
      setUsers(directData || []);
      console.log('📋 Direct query loaded users:', directData?.length || 0);
      
    } catch (error) {
      console.error('❌ Error loading users:', error);
      toast.error('Failed to load users: ' + (error as Error).message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          role: newRole,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;
      
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      
      toast.success('User role updated successfully');
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
    }
  };

  const handlePasswordReset = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;
      toast.success('Password reset email sent');
    } catch (error) {
      console.error('Error sending password reset:', error);
      toast.error('Failed to send password reset email');
    }
  };

  const runQuickDiagnostic = async () => {
    try {
      // Test current user
      const { data: { user } } = await supabase.auth.getUser();
      
      // Test profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('email, role')
        .eq('id', user?.id)
        .single();

      // Test RPC function
      const { data: rpcTest, error: rpcError } = await supabase.rpc('admin_list_users', {
        p_search: null,
        p_role: null,
        p_limit: 1,
        p_offset: 0
      });

      const status = {
        authEmail: user?.email,
        profileEmail: profile?.email,
        isAdmin: profile?.role === 'admin',
        rpcWorking: !rpcError,
        emailsMatch: user?.email === profile?.email
      };

      console.log('🔍 Quick Diagnostic:', status);
      
      if (status.emailsMatch && status.rpcWorking) {
        toast.success('✅ All systems working correctly!');
      } else {
        toast.error('⚠️ Issues detected - check console for details');
      }

    } catch (error) {
      console.error('Diagnostic failed:', error);
      toast.error('Diagnostic failed');
    }
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servet-blue"></div>
          <p className="text-bw-muted mt-4">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-bw-ink">User Management</h2>
          <p className="text-bw-muted mt-1">Manage user accounts, roles, and permissions</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={runQuickDiagnostic}
            className="flex items-center px-3 py-2 bg-bw-primary-tint text-bw-primary rounded-md hover:bg-blue-200"
          >
            <Database className="h-4 w-4 mr-2" />
            Quick Test
          </button>
          <button
            disabled
            title="Not yet available"
            className="flex items-center px-4 py-2 bg-bw-surface-subtle text-bw-muted rounded-md cursor-not-allowed opacity-70"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add User <span className="ml-1.5 text-xs font-normal">(soon)</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-bw-primary" />
            <div className="ml-4">
              <p className="text-sm font-medium text-bw-muted">Total Users</p>
              <p className="text-2xl font-bold text-bw-ink">{filteredUsers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-success" />
            <div className="ml-4">
              <p className="text-sm font-medium text-bw-muted">Admin Users</p>
              <p className="text-2xl font-bold text-bw-ink">
                {filteredUsers.filter(u => u.role === 'admin').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center">
            <CreditCard className="h-8 w-8 text-bw-primary" />
            <div className="ml-4">
              <p className="text-sm font-medium text-bw-muted">Paid Users</p>
              <p className="text-2xl font-bold text-bw-ink">
                {filteredUsers.filter(u => u.membership_tier && u.membership_tier !== 'free').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-bw-muted" />
          <input
            type="text"
            placeholder="Search users by email or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
          />
        </div>
        <button
          onClick={loadUsers}
          className="flex items-center px-4 py-2 bg-bw-surface-subtle text-bw-ink rounded-md hover:bg-gray-200"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-bw-surface shadow-card rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-bw-border">
          <thead className="bg-bw-surface-subtle">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-bw-muted uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-bw-muted uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-bw-muted uppercase tracking-wider">
                Subscription
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-bw-muted uppercase tracking-wider">
                Created
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-bw-surface divide-y divide-bw-border">
            {filteredUsers.map((user) => (
              <tr key={user.id} className="hover:bg-bw-surface-subtle">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-bw-primary-tint flex items-center justify-center">
                      <Users className="h-5 w-5 text-bw-primary" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-bw-ink">
                        {user.company_name || 'No Company Name'}
                      </div>
                      <div className="text-sm text-bw-muted">{user.email || 'No email'}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={user.role}
                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    className="text-sm text-bw-ink bg-transparent border-0 focus:ring-0"
                  >
                    <option value="business">Business</option>
                    <option value="agency">Agency</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.subscription_status === 'active'
                      ? 'bg-success-bg text-success'
                      : 'bg-bw-surface-subtle text-bw-ink'
                  }`}>
                    {user.membership_tier || 'free'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-bw-muted">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePasswordReset(user.email)}
                      className="text-bw-primary hover:text-blue-900"
                      title="Send password reset"
                      disabled={!user.email}
                    >
                      <Lock className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => window.open(`mailto:${user.email}`, '_blank')}
                      className="text-bw-muted hover:text-bw-ink"
                      title="Send email"
                      disabled={!user.email}
                    >
                      <Mail className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-bw-muted mx-auto mb-4" />
            <p className="text-bw-muted">No users found</p>
          </div>
        )}
      </div>

      {/* Status Footer */}
      <div className="bg-success-bg border border-success-border rounded-lg p-4">
        <p className="text-success text-sm">
          ✅ After running the SQL fixes, this component should work perfectly. 
          Use the "Quick Test" button to verify all systems are working.
        </p>
      </div>
    </div>
  );
}